import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, BookOpen, HelpCircle, Mail, Facebook } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { AppHeader } from "@/components/AppHeader";
import { BrandLogo } from "@/components/BrandLogo";
import { Seo } from "@/components/Seo";

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-hero">
      <Seo
        title="SpeakLemon — Master IELTS Speaking Part 2 with Real-Time AI"
        description="AI-powered IELTS Speaking Part 2 coach with a live 3-zone HUD. Get Band 9 vocabulary, grammar fixes, and instant feedback as you speak."
        path="/"
      />
      <AppHeader />

      <main>
        <section className="container pt-4 md:pt-6 pb-12 md:pb-16">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="flex items-center justify-center gap-4">
              <BrandLogo className="h-28 w-28 md:h-40 md:w-40" />
              <span className="font-display text-4xl md:text-7xl font-bold tracking-tight bg-gradient-primary bg-clip-text text-transparent">
                SpeakLemon
              </span>
            </div>
            {!user && (
              <div className="flex justify-center">
                <Button
                  asChild
                  size="lg"
                  className="h-16 px-10 text-lg md:text-xl font-bold bg-[hsl(54_98%_55%)] text-[hsl(220_40%_12%)] hover:bg-[hsl(54_98%_50%)] shadow-glow rounded-full"
                >
                  <Link to="/auth">
                    🍋 Get free access for 2 days! <ArrowRight className="ml-1 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            )}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium">
              <Sparkles className="h-3 w-3" /> Real-time AI feedback · Built by a 10+ year IELTS instructor
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold leading-tight tracking-tight">
              Master IELTS Speaking Part 2 with an{" "}
              <span className="bg-gradient-primary bg-clip-text text-transparent">AI Real-Time HUD</span>
            </h1>
            <p className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto">
              See your transcript, grammar fixes, and Band 9 vocabulary upgrades stream live as you speak — like a heads-up display for your brain.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4">
              <Button asChild size="lg" className="shadow-glow text-base">
                <Link to={user ? "/session" : "/auth"}>
                  Start Part 2 Practice <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/prepare"><BookOpen className="mr-1 h-4 w-4" /> Prep library</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/pricing">See pricing</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="container pb-16 md:pb-24">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-center mb-6">
              Meet SpeakLemon. Ready to Practice Anytime
            </h2>
            <div className="aspect-video rounded-xl overflow-hidden border border-border/40 shadow-card bg-muted">
              <video
                className="w-full h-full object-cover"
                src="https://klyodtesfywhxvrfdtsf.supabase.co/storage/v1/object/public/public-assets/speaklemon-intro.mp4"
                controls
                playsInline
                preload="metadata"
              />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/40 py-8">
        <div className="container flex flex-col items-center gap-4 text-sm text-muted-foreground">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/pricing#faq"><HelpCircle className="h-4 w-4" /> FAQ</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <a href="mailto:hello@speaklemon.com"><Mail className="h-4 w-4" /> Contact</a>
            </Button>
            <Button asChild variant="outline" size="sm">
              <a
                href="https://www.facebook.com/profile.php?id=61589196592185"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook className="h-4 w-4" /> Facebook
              </a>
            </Button>
          </div>
          <div>© {new Date().getFullYear()} SpeakLemon</div>
          <p
            className="max-w-2xl text-left text-muted-foreground/60 leading-snug mx-auto"
            style={{ fontSize: "11px" }}
          >
            SpeakLemon is an independent training tool. We have no affiliation with, or endorsement from, the official IELTS partners (British Council, IDP, or Cambridge) — or even Elon Musk.
          </p>
        </div>
      </footer>
    </div>
  );
}
